import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import {
    Button, makeStyles,
} from '@material-ui/core';
import { useQuery } from '@apollo/client';
import { Form, Col } from 'react-bootstrap';
import { FetchPolicy, AI_CUSTOM_FUNCTION_ROLE } from 'utils/enum/Core';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import AIQuery from 'services/graphQL/query/ai/AIQuery';
import ButtonStyles from 'styles/theme/Button';
import Select from 'components/widgets/Select';
import VirtualTable from 'components/widgets/VirtualTable';

// icons
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    tableContainer: {
        overflow: 'hidden',
        minWidth: '800px',
        '& .DragHandleIcon': {
            color: theme.palette.text.waterloo,
        },
    },
    tableHeader: {
        textAlign: 'left',
        color: theme.palette.text.waterloo,
        borderRight: `1px solid ${theme.palette.border.ghost}`,
        height: '100%',
        alignItems: 'center',
    },
    text: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    actionsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& > button:nth-child(2), & > button:nth-child(3)': {
            marginRight: '2px',
        },
        '& > button': {
            minWidth: '32px',
            '& .MuiButton-startIcon': {
                marginRight: '0px',
            },
        },
    },
    input: {
        fontSize: '13px',
        resize: 'none',
        width: '100%',
    },
    '@global': {
        '.css-26l3qy-menu div': {
            fontSize: '13px',
            lineHeight: '1.4',
        },
    },
}));

const groupFunctionsByRole = (input) => Object
    .values(AI_CUSTOM_FUNCTION_ROLE)
    .reduce((a, b) => {
        const clone = { ...a };

        const functions = input.filter((i) => i.role.includes(b));
        if (functions.length > 0) clone[b] = functions.map(({ name, description }) => ({ name, description }));

        return clone;
    }, {});

const AssistantFunctions = forwardRef((props, ref) => {
    const {
        existingFunctions,
        parentActionInProgress,
        tableHight,
        className,
    } = props;
    const classes = { ...useStyles(), ...buttonStyles() };
    const [state, setState] = useState({
        functions: {},
        selectedFunctionsRole: null,
        selectedFunctions: existingFunctions?.length > 0 ? existingFunctions.map((f) => ({
            description: f.description,
            functionName: f.function,
        })) : [],
    });

    const {
        functions,
        selectedFunctionsRole,
        selectedFunctions,
    } = state;

    const {
        data: functionsData,
        loading: loadingFunctions,
        error: errorLoadingFunctions,
    } = useQuery(AIQuery.GET_CUSTOM_FUNCTIONS, {
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    useEffect(() => {
        if (errorLoadingFunctions) {
            ModalUtils.errorMessage(errorLoadingFunctions?.graphQLErrors);
            return;
        }

        if (!loadingFunctions) {
            const data = functionsData?.getCustomFunctions;
            if (data) {
                setState((prevState) => ({
                    ...prevState,
                    functions: groupFunctionsByRole(data),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingFunctions, errorLoadingFunctions]);

    useImperativeHandle(ref, () => ({
        getFunctionsSelected() {
            return selectedFunctions;
        },
    }));

    const onChange = (name, value) => {
        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const addFunctionsInRole = () => {
        const clone = selectedFunctions.slice();
        const functionsInRole = functions[selectedFunctionsRole] ?? [];

        functionsInRole.forEach(({
            name,
            description,
        }) => {
            if (!clone.find((i) => i.functionName === name)) {
                clone.push({
                    description,
                    functionName: name,
                });
            }
        });

        setState((prevState) => ({
            ...prevState,
            selectedFunctions: clone,
        }));
    };

    const deleteFunction = (record) => {
        const clone = selectedFunctions.slice();
        setState((prevState) => ({
            ...prevState,
            selectedFunctions: clone.filter((f) => f.functionName !== record.functionName),
        }));
    };

    const functionsColumns = [
        {
            headerClassName: classes.tableHeader,
            label: 'Description',
            dataKey: 'description',
            width: 300,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.description}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Function',
            dataKey: 'function',
            width: 300,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.functionName}</span>
                );
            },
        },
        {
            headerClassName: classes.tableHeader,
            label: 'Action',
            dataKey: 'action',
            width: 130,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;

                return (
                    <div className={classes.actionsContainer}>
                        <Button
                            disabled={parentActionInProgress}
                            className={classes.containedError}
                            size="small"
                            startIcon={<DeleteOutlineOutlinedIcon />}
                            onClick={() => deleteFunction(record)}
                        />
                    </div>
                );
            },
        },
    ];

    const parentWidth = document.getElementById('main-container')?.clientWidth;
    let functionsTableWidth = functionsColumns.reduce((a, b) => a + b.width, 0);
    if (parentWidth > functionsTableWidth) functionsTableWidth = parentWidth - 50;
    return (
        <div className={className}>
            <Form.Group as={Col}>
                <Form.Label className={classes.labels}>Functions</Form.Label>
                <div>
                    <Select
                        placeholder="Role"
                        size="sm"
                        loading={loadingFunctions}
                        className={classes.input}
                        name="selectedFunctionsRole"
                        onChange={(name, value) => onChange(name, value)}
                        value={selectedFunctionsRole ?? ''}
                        options={Object.values(AI_CUSTOM_FUNCTION_ROLE).map((f) => ({
                            value: f,
                            label: f,
                        }))}
                    />
                    <Button
                        disabled={
                            StringUtils.isEmpty(selectedFunctionsRole)
                            || parentActionInProgress
                        }
                        className={classes.containedSecondaryInfo}
                        size="small"
                        onClick={addFunctionsInRole}
                    >
                        Add
                    </Button>
                </div>
                <div className={classes.tableContainer} style={{ height: `${tableHight}px` }}>
                    {parentWidth > 0 && (
                        <VirtualTable
                            loading={loadingFunctions}
                            rowHeight={45}
                            totalRecords={selectedFunctions.length}
                            data={selectedFunctions}
                            columns={functionsColumns}
                            width={functionsTableWidth}
                        />
                    )}
                </div>
            </Form.Group>
        </div>
    );
});

AssistantFunctions.defaultProps = {
    existingFunctions: [],
    parentActionInProgress: false,
    tableHight: 300,
    className: '',
};

AssistantFunctions.propTypes = {
    existingFunctions: PropTypes.any,
    parentActionInProgress: PropTypes.bool,
    tableHight: PropTypes.number,
    className: PropTypes.string,
};

export default AssistantFunctions;
